import { type PaginationState, TablePagination } from '@/components/shared/TablePagination';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import DocumentRowActions from './document-row-actions';
import DocumentStatusBadge from './document-status-badge';
import DraftProgressCell from './draft-progress-cell';
import type { ContractDocumentRow } from '../constants/mock-contract-documents';

interface ContractsDataCardViewProps {
  documents: ContractDocumentRow[];
  isLoading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: string;
  /** When true, the per-row body shows the draft progress bar + last-edited
      date instead of the created date + status badge — mirrors the column
      swap the desktop table does for the "Draft" tab. */
  isDraftTab?: boolean;
  isUpdating?: boolean;
  onRowClick: (row: ContractDocumentRow) => void;
  onArchive: (row: ContractDocumentRow) => void;
  onMoveToTrash: (row: ContractDocumentRow) => void;
  /** Predicate from the parent — keeps the resume affordance in sync with
      the table's `isEditableStatus` rule without duplicating it here. */
  isEditableStatus: (status: ContractDocumentRow['status']) => boolean;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  showingLabel?: string;
  className?: string;
}

const ContractsDataCardView = ({
  documents,
  isLoading = false,
  loadingRowCount = 5,
  emptyMessage,
  isDraftTab = false,
  isUpdating = false,
  onRowClick,
  onArchive,
  onMoveToTrash,
  isEditableStatus,
  pagination,
  onPageChange,
  showingLabel,
  className,
}: ContractsDataCardViewProps) => {
  const { t } = useTranslation();
  const skeletonRows = Math.max(loadingRowCount, 1);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-background',
        className
      )}
    >
      <div className="flex flex-col gap-3 p-3">
        {isLoading ? (
          Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={`contracts-card-skeleton-${i}`}
              className="rounded-lg border border-border bg-background p-4 space-y-3"
            >
              <Skeleton className="h-4 w-3/4 rounded-full" />
              <Skeleton className="h-3 w-1/2 rounded-full" />
              <Skeleton className="h-3 w-2/3 rounded-full" />
            </div>
          ))
        ) : documents.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            {emptyMessage ?? t('ORGCONTRACTS_EMPTY')}
          </div>
        ) : (
          documents.map((row) => {
            const editable = isEditableStatus(row.status);
            return (
              <article
                key={row.id}
                className="rounded-lg border border-border bg-background p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div
                    className={cn('min-w-0 flex-1 space-y-1', editable && 'cursor-pointer')}
                    onClick={() => onRowClick(row)}
                  >
                    <p className="text-[15px] font-semibold leading-5 text-[hsl(var(--fg))] line-clamp-2">
                      {row.title}
                    </p>
                    <p className="text-sm text-globe-grey line-clamp-1">
                      {row.recipientsSummary ?? '-'}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <DocumentRowActions
                      slug={row.templateSlug as string}
                      status={row.status}
                      isLoading={isUpdating}
                      onArchive={() => onArchive(row)}
                      onMoveToTrash={() => onMoveToTrash(row)}
                      onResume={
                        editable && row.templateSlug ? () => onRowClick(row) : undefined
                      }
                      documentId={row.id}
                      currentTitle={row.title}
                      templateTitle={row.templateTitle}
                      documentRaw={row.documentRaw}
                      formDataRaw={row.formDataRaw}
                      signatoriesRaw={row.signatoriesRaw}
                      sigDocumentUrl={row.sigDocumentUrl}
                      sigDocumentId={row.sigDocumentId}
                    />
                  </div>
                </div>

                {isDraftTab ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-globe-grey">
                        {t('ORGCONTRACTS_COLUMNS_LASTMODIFIED')}
                      </span>
                      <span className="font-semibold text-[hsl(var(--fg))]">
                        {row.lastEditedAt ?? '-'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-globe-grey mb-1">
                        {t('ORGCONTRACTS_COLUMNS_PROCESSINGPERCENTAGE')}
                      </p>
                      <DraftProgressCell progressPercent={row.progressPercent} />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-xs text-globe-grey">
                        {t('ORGCONTRACTS_COLUMNS_CREATEDDATE')}
                      </p>
                      <p className="text-sm font-semibold text-[hsl(var(--fg))]">
                        {row.createdAt ?? '-'}
                      </p>
                      {row.lastUpdatedDate ? (
                        <p className="text-xs text-muted-foreground">
                          {t('ORGCONTRACTS_LAST_UPDATED_ON', { date: row.lastUpdatedDate })}
                        </p>
                      ) : null}
                    </div>
                    <DocumentStatusBadge status={row.status} />
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      {!isLoading && pagination && onPageChange ? (
        <div className="border-t border-border bg-background px-3 py-2">
          <TablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            showingLabel={showingLabel}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ContractsDataCardView;
